import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, LifeBuoy } from 'lucide-react';
import { api, getErrorMessage } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Cargando, ErrorCarga } from '@/components/ui';

interface Pregunta {
  id: string;
  pregunta: string;
  respuesta: string;
}

interface Seccion {
  titulo: string;
  preguntas: Pregunta[];
}

/**
 * Centro de ayuda.
 *
 * Las preguntas vienen de la API en vez de estar escritas aquí, para que el
 * día que cambie una tarifa o una regla no haya que publicar la tienda de
 * nuevo. Se abren de una en una: una página con veinte respuestas desplegadas
 * no la lee nadie.
 */
export function Ayuda() {
  const [abierta, setAbierta] = useState<string | null>(null);

  const secciones = useQuery({
    queryKey: ['ayuda'],
    queryFn: async () => (await api.get<Seccion[]>('/ayuda/preguntas')).data,
    // Cambian muy de vez en cuando; no hace falta pedirlas en cada visita.
    staleTime: 30 * 60 * 1000,
  });

  if (secciones.isLoading) return <Cargando />;
  if (secciones.isError) {
    return (
      <ErrorCarga
        mensaje={getErrorMessage(secciones.error)}
        onReintentar={() => void secciones.refetch()}
      />
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <div className="flex items-center gap-3">
        <div className="grid h-11 w-11 place-items-center rounded-kick bg-kick-red/10 text-kick-red">
          <LifeBuoy className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-3xl text-kick-dark">Ayuda</h1>
          <p className="text-sm text-kick-gray">Lo que más nos preguntan.</p>
        </div>
      </div>

      <div className="mt-8 space-y-8">
        {secciones.data?.map((seccion) => (
          <section key={seccion.titulo}>
            <h2 className="text-xs font-bold uppercase tracking-widest text-kick-gray">
              {seccion.titulo}
            </h2>

            <div className="mt-3 overflow-hidden rounded-kick bg-white">
              {seccion.preguntas.map((p, i) => {
                const activa = abierta === p.id;

                return (
                  <div key={p.id} className={i > 0 ? 'border-t border-kick-gray/10' : ''}>
                    <button
                      onClick={() => setAbierta(activa ? null : p.id)}
                      aria-expanded={activa}
                      aria-controls={`respuesta-${p.id}`}
                      className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition hover:bg-kick-light"
                    >
                      <span className="font-semibold text-kick-ink">{p.pregunta}</span>
                      <ChevronDown
                        aria-hidden
                        className={cn(
                          'h-4 w-4 shrink-0 text-kick-gray transition-transform',
                          activa && 'rotate-180',
                        )}
                      />
                    </button>

                    {activa && (
                      <p
                        id={`respuesta-${p.id}`}
                        className="px-5 pb-4 text-sm leading-relaxed text-kick-gray"
                      >
                        {p.respuesta}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <div className="mt-8 rounded-kick bg-kick-dark p-6 text-center">
        <p className="font-semibold text-white">¿Tu problema es con un pedido concreto?</p>
        <p className="mt-1 text-sm text-white/70">
          Ábrelo desde «Mis pedidos» y toca <strong>Reportar un problema</strong>. Así llega con
          todos los datos y lo resolvemos más rápido.
        </p>
      </div>
    </div>
  );
}
